import {View} from 'pacto';
import anime from 'animejs';


const
	CONTENT_SELECTOR = '[data-animatable]',
	ELEMENT_OFFSET = 100,
	ANIMATION_ELEMENT_INTERSECTION_TRIGGER = 0.25,
	ANIMATION_DURATION = ELEMENT_OFFSET * 7.5,
	ANIMATION_EASE = 'easeOutExpo'
;


export class ScrollAnimation extends View {

	render() {
		// Disable scroll animation if user prefers reduced motion
		if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)')?.matches) {
			return this;
		}
		this.setup();
		return this;
	}

	get targets() {
		if (!this._targets) {
			const targets = [...this.el.querySelectorAll(CONTENT_SELECTOR)];

			// mark elements which are above the viewport or to large to trigger the animation as already animated
			targets
				.filter((el) => {
					const {top, height} = el.getBoundingClientRect();
					return top < 0 || height > window.innerHeight / ANIMATION_ELEMENT_INTERSECTION_TRIGGER;
				})
				.map((el) => el.setAttribute('data-animated', 'true'));

			// set only those elements as targets, which are not automatically animated
			this._targets = targets.filter((el) => !this._isAnimated(el));
		}

		return this._targets;
	}

	setup() {
		// prevent registering multiple observers
		if (this.observer) {
			return;
		}

		this.observer = new IntersectionObserver((entries) => {
			let offset = 0;

			entries
				// ignore elements which are not really intersecting or already animated elements
				.filter((entry) =>
					entry.intersectionRatio >= ANIMATION_ELEMENT_INTERSECTION_TRIGGER && !this._isAnimated(entry.target)
				)
				// sort elements by position
				.sort((a, b) => {
					const
						ar = a.boundingClientRect,
						br = b.boundingClientRect
					;

					return (ar.top + ar.left) - (br.top + br.left);
				})
				.map((entry) => {
					const el = entry.target;
					// animate the element after the current time offset
					setTimeout(() => this.animate(el), offset);
					// increase the offset by 500ms
					offset += ELEMENT_OFFSET;
					this.observer.unobserve(el);
					return el;
				});
		}, {threshold: ANIMATION_ELEMENT_INTERSECTION_TRIGGER});

		// observe all content elements
		this.targets.map((el) => this.observer.observe(el));
	}

	animate(el) {
		const animationType = el.getAttribute('data-animation-type');

		switch (animationType) {
			case 'markup': {
				anime({
					targets: el.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, a.link, button.link'),
					easing: ANIMATION_EASE,
					duration: ANIMATION_DURATION,
					translateY: [100, 0],
					opacity: [0, 1],
					delay: anime.stagger(ELEMENT_OFFSET)
				});
				break;
			}
			case 'container': {
				const targets = el.querySelector('div');
				anime({
					targets,
					easing: ANIMATION_EASE,
					duration: ANIMATION_DURATION,
					translateY: [100, 0],
					opacity: [0, 1],
					begin: () => anime.set(targets, {transition: 'none'}),
					complete: () => (targets || {}).style = ''
				});
				break;
			}
			default: {
				const
					tag = el.tagName.toLowerCase(),
					classes = el.classList.toString().split(' ').join('.'),
					selector = `${tag}.${classes}`,
					serializedSelector = selector.length > 50 ? selector.substr(0, 50) + '...' : selector
				;
				throw new Error(`Unknown animation type: ${animationType} on ${serializedSelector}`);
			}
		}

		el.setAttribute('data-animated', 'true');
	}

	_isAnimated(el) {
		return el.getAttribute('data-animated') === 'true';
	}

}
